// Readable plain text for the lock screen and for speech (architecture §14.4, §13.2; WP3 D25). Pure, no DOM, no Node
// APIs. Named so that it is not confused with WP2's Gmail plainText(), which takes the text part of a mail.
//
// One form of every character a person reads the same way, so the scrubber, the residue check and the pronunciation
// tables see what the lock screen or the voice shows: NFKC folds compatibility forms (fullwidth and mathematical
// digits and letters, superscripts, NBSP, narrow NBSP and every other space, fullwidth "@" and "."); invisible
// characters go (zero-width space and joiner, word joiner, soft hyphen, BOM, bidi controls, variation selectors, tag
// characters, Hangul fillers; a zero-width joiner and an emoji variation selector inside an emoji stay); keycap digits
// read as plain digits; every line break becomes "\n", every other space and every control character a plain space;
// combining marks that NFKC could not compose with their letter go ("0̶1̶7̶1̶", "5⃝8⃝" read "0171", "58"); dingbat
// and double-circled digits read as digits ("❺❽", "⓹⓼": NFKC has no form for them). A street with a no-break space,
// a zero-width space or a control character before its number, or a phone number in fullwidth or struck-through
// digits, then reads like its plain form.
//
// Kept as written, because NFKC would change what a person reads or hears: the ellipsis (NFKC: three full stops), "m²"
// and "cm³" (a unit, not a digit), vulgar fractions ("1½": NFKC "11⁄2"), the emoji that have a compatibility form ("™"
// NFKC "TM", "ℹ️" "i", "‼️" "!!", "⁉️" "!?", "Ⓜ️" "M" …), and the spacing accents NFKC splits into a space and a
// combining mark ("´", "¨", "¸" …); "´" between two letters is an apostrophe ("geht´s", "Lena´s") and reads as "’".
// Decimal digits of other scripts have no plain form; the residue check fails closed on them.

const range = (from: number, to: number): string[] => Array.from({ length: to - from + 1 }, (_, i) => String.fromCodePoint(from + i));
/** Superscript digits, signs and brackets ("10⁶", "x²", "m³", "Fußnote¹", "10⁻³"). */
const SUPERSCRIPTS: readonly string[] = ['\u{B9}', '\u{B2}', '\u{B3}', '\u{2070}', '\u{2071}', ...range(0x2074, 0x207f)];

/**
 * Characters kept as written through NFKC (see the file comment). While NFKC runs, each stands in as the private-use
 * character U+E000 + its index (private-use characters of the input are dropped first). The emoji and the spacing
 * accents are every character of those kinds that NFKC changes (`readablePlainText.test.ts` checks the lists against
 * the Unicode data of the runtime).
 */
const KEEP_AS_WRITTEN: readonly string[] = [
  '\u{2026}', // ellipsis
  ...SUPERSCRIPTS, // only as an exponent or a footnote mark right after a letter, digit or bracket (EXPONENT)
  '\u{AA}', '\u{BA}', '\u{2116}', '\u{2120}', // ordinal indicators ("1º", "3ª", "Nº"), "№", "℠" (round 8)
  ...range(0xbc, 0xbe), ...range(0x2150, 0x215f), '\u{2189}', // vulgar fractions
  '\u{203C}', '\u{2049}', '\u{2122}', '\u{2139}', '\u{24C2}', '\u{3297}', '\u{3299}', '\u{1F16C}', '\u{1F201}', '\u{1F202}',
  '\u{1F21A}', '\u{1F22F}', ...range(0x1f232, 0x1f23a), '\u{1F250}', '\u{1F251}', // emoji with a compatibility form
  '\u{A8}', '\u{AF}', '\u{B4}', '\u{B8}', ...range(0x2d8, 0x2dd), '\u{37A}', '\u{384}', '\u{385}', '\u{1FBD}', ...range(0x1fbf, 0x1fc1),
  ...range(0x1fcd, 0x1fcf), ...range(0x1fdd, 0x1fdf), '\u{1FED}', '\u{1FEE}', '\u{1FFD}', '\u{1FFE}', '\u{2017}', '\u{203E}', '\u{309B}',
  '\u{309C}', ...range(0xfc5e, 0xfc63), ...range(0xfe49, 0xfe4c), '\u{FE70}', '\u{FE72}', '\u{FE74}', '\u{FE76}', '\u{FE78}', '\u{FE7A}',
  '\u{FE7C}', '\u{FE7E}', '\u{FFE3}', // spacing accents
];
const STAND_IN = new Map(KEEP_AS_WRITTEN.map((c, i) => [c, String.fromCodePoint(0xe000 + i)]));
const standIn = (c: string): string => STAND_IN.get(c) ?? c;
/** Every kept character but the superscripts (those only as an exponent: EXPONENT). */
const KEPT = new RegExp(`[${KEEP_AS_WRITTEN.filter((c) => !SUPERSCRIPTS.includes(c)).join('')}]`, 'gu');
const HELD = new RegExp(`[\\u{E000}-\\u{${(0xe000 + KEEP_AS_WRITTEN.length - 1).toString(16)}}]`, 'gu');
const restore = (h: string): string => KEEP_AS_WRITTEN[h.codePointAt(0)! - 0xe000] ?? '';

/** An exponent or a footnote mark: one or two superscripts right after a letter, a digit or a closing bracket ("m²",
 *  "10⁶", "x²", "Umsatz¹", "10⁻³"; round 8). A longer run or one after a space ("Schulterblatt ⁵⁸", "⁰¹⁷¹ ¹²³⁴") reads
 *  as plain digits, which the scrubber then sees. A kept exponent is read as a digit by the residue check
 *  (foldForChecks), so "Schulterblatt⁵⁸" still makes a push neutral. */
const SUP = `${SUPERSCRIPTS.join('')}`;
const EXPONENT = new RegExp(`(?<=[\\p{L}\\p{N})\\]])(?<![${SUP}])[${SUP}]{1,2}(?![${SUP}])`, 'gu');
/** "´" between two letters: an apostrophe ("geht´s"). */
const ACUTE_APOSTROPHE = /(?<=\p{L})\u{B4}(?=\p{L})/gu;
const PRIVATE_USE = /\p{Co}/gu;
const KEYCAP = /([0-9#*])\u{FE0F}?\u{20E3}/gu;
const IGNORABLE = /\p{Default_Ignorable_Code_Point}/gu;
const EMOJI = /[\p{Extended_Pictographic}\p{Emoji_Modifier}]/u;
const PICTO = /\p{Extended_Pictographic}/u;
const LINE_BREAK = /\r\n?|[\v\f\u{85}\u{2028}\u{2029}]/gu;
/** Control characters (C0 and C1 but the line breaks above and the tab) and the interlinear annotation marks: a space. */
const CONTROL = /[\u{0}-\u{8}\u{E}-\u{1F}\u{7F}-\u{84}\u{86}-\u{9F}\u{FFF9}-\u{FFFB}]/gu;
/** Every space but the line break, and the blank braille pattern that renders as a space. */
const SPACE = /[^\S\n]|\u{2800}/gu;
/** Combining diacritics and symbol marks that NFKC could not compose with their letter (strike-through, underline,
 *  enclosing circle …): "0̶1̶7̶1̶" reads "0171". */
const LOOSE_MARK = /[\u{300}-\u{36F}\u{1AB0}-\u{1AFF}\u{1DC0}-\u{1DFF}\u{20D0}-\u{20FF}\u{FE20}-\u{FE2F}]/gu;
/** Dingbat, sans-serif, negative and double-circled digits ("❺", "➑", "⓹", "⓫", "⓿"). */
const DINGBAT_DIGIT = /[\u{2776}-\u{2793}\u{24EB}-\u{24FF}\u{1F10B}\u{1F10C}]/gu;
/** The hyphen, the non-breaking hyphen and the figure dash: a reader sees "-" ("Konrad‐Adenauer‐Platz", "0171‐1234567"). */
const HYPHEN = /[\u{2010}-\u{2012}]/gu;
const VS16 = '\u{FE0F}';
const ZWJ = '\u{200D}';

function dingbatDigit(c: string): string {
  const n = c.codePointAt(0)!;
  if (n >= 0x2776 && n <= 0x2793) return String(((n - 0x2776) % 10) + 1);
  if (n >= 0x24eb && n <= 0x24f4) return String(n - 0x24eb + 11);
  if (n >= 0x24f5 && n <= 0x24fe) return String(n - 0x24f5 + 1);
  return '0';
}

function codePointBefore(s: string, at: number): string {
  if (at <= 0) return '';
  const lo = s.charCodeAt(at - 1);
  return lo >= 0xdc00 && lo <= 0xdfff && at >= 2 ? s.slice(at - 2, at) : s.slice(at - 1, at);
}

function codePointAt(s: string, at: number): string {
  const cp = s.codePointAt(at);
  return cp === undefined ? '' : String.fromCodePoint(cp);
}

const BLACK_FLAG = '\u{1F3F4}';
const isTag = (c: string): boolean => { const n = c.codePointAt(0) ?? 0; return n >= 0xe0020 && n <= 0xe007f; };

/** A tag character of a subdivision flag ("🏴" + tags + cancel tag: England, Scotland, Wales; round 8): the run of tag
 *  characters before it leads back to the black flag. */
function inFlag(at: number, s: string): boolean {
  let i = at;
  for (;;) {
    const before = codePointBefore(s, i);
    if (before === BLACK_FLAG) return true;
    if (!isTag(before)) return false;
    i -= before.length;
  }
}

/** Drops an invisible character, except a joiner or a variation selector that belongs to an emoji (a family or "red
 *  heart" emoji) and the tag characters of a subdivision flag. */
function dropInvisible(ch: string, at: number, s: string): string {
  if (isTag(ch)) return inFlag(at, s) ? ch : '';
  if (ch === VS16) return EMOJI.test(codePointBefore(s, at)) ? ch : '';
  if (ch === ZWJ) {
    const back = codePointBefore(s, at) === VS16 ? at - 1 : at;
    return EMOJI.test(codePointBefore(s, back)) && PICTO.test(codePointAt(s, at + 1)) ? ch : '';
  }
  return '';
}

/** The text as a person reads it, in one form per character (see the file comment). Idempotent. */
export function readablePlainText(text: string): string {
  if (!text) return '';
  return text
    .replace(PRIVATE_USE, '')
    .replace(ACUTE_APOSTROPHE, '\u{2019}')
    .replace(EXPONENT, (m) => [...m].map(standIn).join(''))
    .replace(KEPT, standIn)
    .normalize('NFKC')
    .replace(HELD, restore)
    .replace(KEYCAP, '$1')
    .replace(IGNORABLE, dropInvisible)
    .replace(LINE_BREAK, '\n')
    .replace(CONTROL, ' ')
    .replace(SPACE, ' ')
    .replace(LOOSE_MARK, '')
    .replace(DINGBAT_DIGIT, dingbatDigit)
    .replace(HYPHEN, '-');
}

/** Characters readablePlainText keeps as written that a check must still read as their plain form: the ordinal
 *  indicators, "№", "℠" and the superscripts ("3º A" reads "3o A", "Schulterblatt⁵⁸" "Schulterblatt58"). The residue
 *  check reads this view next to the plain text (round 8), so a kept character never hides a number. */
const FOLD = new RegExp(`[\\u{AA}\\u{BA}\\u{2116}\\u{2120}${SUP}]`, 'gu');
export function foldForChecks(text: string): string {
  return text.replace(FOLD, (c) => c.normalize('NFKC'));
}
